import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import { OrderRowTemplate } from "../../common/types/apiTypes";
import {
  createOrderRowTemplate,
  fetchOrderRowTemplates,
  updateOrderRowTemplate,
  updateOrderRowTemplateActiveStatus,
} from "../orders/orderAPI";
import {
  CreateOrderRowTemplate,
  UpdateActiveStatus,
  UpdateOrderRowTemplate,
} from "./types/commands";
import {
  OrderRowTemplateBillableChanged,
  OrderRowTemplateCertificatesChanged,
  OrderRowTemplateLanguagesChanged,
  OrderRowTemplateNameChanged,
  OrderRowTemplatePeriodStartDateChanged,
  OrderRowTemplateSalaryLimitChanged,
  OrderRowTemplateSalaryLimitsChanged,
  OrderRowTemplateSkillsChanged,
  OrderRowTemplateSupplementPriceOverridesChanged,
  OrderRowTemplateSupplementsChanged,
  OrderRowTemplateTesIdChanged,
  OrderRowTemplateTesVariationChanged,
  OrderRowTemplateTrainingsChanged,
  OrderRowTemplateUpdated,
  OrderRowTemplateWorkTitleChanged,
} from "./types/events";
import { OrderTemplateFilters } from "./types/queries";

const orderTemplatesAdapter = createEntityAdapter<OrderRowTemplate>();

type SliceState = {
  ids: [];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

export const fetchOrderTemplates = createAsyncThunk<
  OrderRowTemplate[],
  OrderTemplateFilters,
  { state: RootState }
>(
  "orderTemplatesSlice/fetchOrderTemplates",
  async (filters: OrderTemplateFilters) =>
    await fetchOrderRowTemplates(filters.contractId),
);

export const updateOrderTemplateActiveStatus = createAsyncThunk<
  EventPayload<{}>,
  UpdateActiveStatus,
  { state: RootState }
>(
  "orderTemplatesSlice/updateOrderTemplateActiveStatus",
  async (params: UpdateActiveStatus, { requestId }) =>
    await updateOrderRowTemplateActiveStatus(params, { requestId }),
);

export const createOrderTemplate = createAsyncThunk<
  EventPayload<CreateOrderRowTemplate>,
  CreateOrderRowTemplate,
  { state: RootState }
>(
  "orderTemplatesSlice/createOrderTemplate",
  async (orderTemplate: CreateOrderRowTemplate, { requestId }) =>
    await createOrderRowTemplate(orderTemplate, {
      requestId,
    }),
);

export const updateOrderTemplate = createAsyncThunk<
  EventPayload<OrderRowTemplateUpdated>[],
  UpdateOrderRowTemplate,
  { state: RootState }
>(
  "orderTemplatesSlice/updateOrderTemplate",
  async (orderTemplate: UpdateOrderRowTemplate, { requestId }) =>
    await updateOrderRowTemplate(orderTemplate, {
      requestId,
    }),
);

export const orderTemplates = createSlice({
  name: "orderTemplates",
  initialState: orderTemplatesAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {
    unsetOrderTemplates: (state) => orderTemplatesAdapter.removeAll(state),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrderTemplates.fulfilled, (state, action) => {
      orderTemplatesAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(fetchOrderTemplates.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchOrderTemplates.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateOrderTemplateActiveStatus.fulfilled,
      (state, action) => {
        const { id, version, name } = action.payload;

        const active = name === "ordRowTemplActivated";
        const changes = { version, active };

        orderTemplatesAdapter.updateOne(state, {
          id,
          changes,
        });

        state.status = "succeeded";
      },
    );
    builder.addCase(updateOrderTemplateActiveStatus.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateOrderTemplateActiveStatus.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(createOrderTemplate.fulfilled, (state, action) => {
      const orderTemplate: OrderRowTemplate = {
        ...action.payload.payload,
        id: action.payload.id,
        version: action.payload.version,
        salaryLimit: action.payload.payload.salaryLimit || null, // TODO: fix among order types
        salaryLimits: action.payload.payload.salaryLimits || [],
      };
      orderTemplatesAdapter.addOne(state, orderTemplate);
      state.status = "succeeded";
    });
    builder.addCase(createOrderTemplate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createOrderTemplate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateOrderTemplate.fulfilled, (state, action) => {
      const id = action.meta.arg.id;

      const updateOrderTemplate = (changes: Partial<OrderRowTemplate>) =>
        orderTemplatesAdapter.updateOne(state, {
          id,
          changes,
        });

      action.payload.forEach((event) => {
        const { version } = event;
        if (event.name === "ordRowTemplNameChg") {
          const { name } = event.payload as OrderRowTemplateNameChanged;
          updateOrderTemplate({ name, version });
        }
        if (event.name === "ordRowTemplWtIdChg") {
          const { workTitleId } =
            event.payload as OrderRowTemplateWorkTitleChanged;
          updateOrderTemplate({ workTitleId, version });
        }
        if (event.name === "ordRowTemplTesIdChg") {
          const { tesId } = event.payload as OrderRowTemplateTesIdChanged;
          updateOrderTemplate({ tesId, version });
        }
        if (event.name === "ordRowTemplTesVarChg") {
          const { tesVariation } =
            event.payload as OrderRowTemplateTesVariationChanged;

          updateOrderTemplate({ tesVariation, version });
        }
        if (event.name === "ordRowTemplSalLimChg") {
          const { salaryLimit } =
            event.payload as OrderRowTemplateSalaryLimitChanged;
          updateOrderTemplate({ salaryLimit, version });
        }
        if (event.name === "ordRowTemplSalLimsChg") {
          const { salaryLimits } =
            event.payload as OrderRowTemplateSalaryLimitsChanged;
          updateOrderTemplate({
            salaryLimits,
            salaryLimit: salaryLimits[0]?.value ?? null,
            version,
          });
        }
        if (event.name === "ordRowTemplSklsChg") {
          const { skills } = event.payload as OrderRowTemplateSkillsChanged;
          updateOrderTemplate({ skills, version });
        }
        if (event.name === "ordRowTemplLangsChg") {
          const { languages } =
            event.payload as OrderRowTemplateLanguagesChanged;
          updateOrderTemplate({ languages, version });
        }
        if (event.name === "ordRowTemplCertsChg") {
          const { certificates } =
            event.payload as OrderRowTemplateCertificatesChanged;
          updateOrderTemplate({ certificates, version });
        }
        if (event.name === "ordRowTemplTrainingsChg") {
          const { trainings } =
            event.payload as OrderRowTemplateTrainingsChanged;
          updateOrderTemplate({ trainings, version });
        }
        if (event.name === "ordRowTemplSupplChg") {
          const { supplements } =
            event.payload as OrderRowTemplateSupplementsChanged;
          updateOrderTemplate({ supplements, version });
        }
        if (event.name === "ordRowTemplSplPrcChg") {
          const { overrides } =
            event.payload as OrderRowTemplateSupplementPriceOverridesChanged;
          updateOrderTemplate({ supplementPriceOverrides: overrides, version });
        }
        if (event.name === "ordRowTemplPrdStrtDtChg") {
          const { periodStartDate } =
            event.payload as OrderRowTemplatePeriodStartDateChanged;
          updateOrderTemplate({ periodStartDate, version });
        }
        if (event.name === "ordRowTemplActivated") {
          updateOrderTemplate({ active: true, version });
        }
        if (event.name === "ordRowTemplDeactivated") {
          updateOrderTemplate({ active: false, version });
        }
        if (event.name === "ordRowTemplBillableChg") {
          const { isBillable } =
            event.payload as OrderRowTemplateBillableChanged;
          updateOrderTemplate({ isBillable, version });
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(updateOrderTemplate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateOrderTemplate.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export const {
  selectAll: selectAllOrderTemplates,
  selectById: selectOrderTemplateById,
} = orderTemplatesAdapter.getSelectors(
  (state: RootState) => state.orderTemplates,
);

export const orderTemplatesReducer = orderTemplates.reducer;
export const { unsetOrderTemplates } = orderTemplates.actions;

export const selectActiveOrderTemplates = createSelector(
  (state: RootState) => selectAllOrderTemplates(state),
  (orderTemplates) =>
    orderTemplates.filter((orderTemplate) => orderTemplate.active),
);
